import { phoneNumberValidation } from '@/common/validation';
import { AddressCheck } from '@/components/address-check';
import { ContentPanel } from '@/components/content-panel';
import { DwellingInput } from '@/components/forms/dwelling-input';
import { Form } from '@/components/forms/form';
import { GenderInput } from '@/components/forms/gender-input';
import { AddressInput, DateInput } from '@/components/inputs';
import { Flex, NumberInput, Text, TextInput } from '@mantine/core';
import { FormikProps } from 'formik';
import * as yup from 'yup';

const validationSchema = yup.object({
	formattedAddress: yup.string().required('Please enter your address.'),
	phoneNumber: phoneNumberValidation,
	gender: yup.string(),
	dateOfBirth: yup.date().max(new Date(), 'Please enter a valid date of birth.'),
	dwellingType: yup.string(),
	householdSize: yup.number().moreThan(0, 'Please enter your household size.'),
});

export type SignupFormValues = {
	formattedAddress: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	council: string;
	postcode: string;
	state: string;
	phoneNumber: string;
	gender: string;
	dateOfBirth: Date;
	dwellingType: string;
	householdSize: number;
	lat: number;
	lng: number;
	isManualAddress: boolean;
};

const initialValues = {
	formattedAddress: '',
	addressLine1: '',
	addressLine2: '',
	isManualAddress: false,
	council: '',
	postcode: '',
	state: '',
	phoneNumber: '',
	gender: '',
	dwellingType: '',
	householdSize: undefined,
	dateOfBirth: undefined,
	lat: undefined,
	lng: undefined,
} as unknown as SignupFormValues;

type SignupFormProps = {
	onSubmit: (values: SignupFormValues) => void;
	formRef: React.RefObject<FormikProps<SignupFormValues>>;
};

export const AccountDetailsForm = ({ onSubmit, formRef }: SignupFormProps) => {
	return (
		<ContentPanel>
			<Form
				initialValues={initialValues}
				onSubmit={onSubmit}
				innerRef={formRef}
				validationSchema={validationSchema}
			>
				{({ getFieldProps, errors, touched, setFieldValue, values }) => {
					return (
						<>
							<Text fz="24px" fw={900}>
								Address
							</Text>
							<AddressInput
								label="Enter Your Address"
								value={values.formattedAddress}
								error={touched.formattedAddress && errors.formattedAddress}
							/>

							<AddressCheck
								council={values.council}
								postcode={values.postcode}
								state={values.state}
								lat={values.lat}
								lng={values.lng}
							/>

							<Text fz="24px" fw={900}>
								Profile (Optional)
							</Text>
							<TextInput
								label="Phone Number"
								placeholder=""
								{...getFieldProps('phoneNumber')}
								error={touched.phoneNumber && errors.phoneNumber}
							/>
							<Flex gap="md" w="100%">
								<GenderInput
									label="Gender"
									value={values.gender}
									onChange={(e) => setFieldValue('gender', e)}
									flex={1}
									error={touched.gender && errors.gender}
								/>

								<Flex gap={0} direction="column">
									<DateInput
										label="Date of Birth"
										value={values.dateOfBirth}
										onChange={(e) => setFieldValue('dateOfBirth', e)}
										flex={1}
										error={
											touched.dateOfBirth &&
											errors.dateOfBirth &&
											'Please enter a valid date of birth.'
										}
									/>
									{!(touched.dateOfBirth && errors.dateOfBirth) && !!values.dateOfBirth && (
										<Text c="gray.0" size="xs" ml="sm">
											DD-MM-YYYY
										</Text>
									)}
								</Flex>
							</Flex>
							<Flex gap="md">
								<DwellingInput
									flex={1}
									label="Dwelling Type"
									value={values.dwellingType}
									onChange={(e) => setFieldValue('dwellingType', e)}
									error={touched.dwellingType && errors.dwellingType}
								/>
								<NumberInput
									flex={1}
									label="Household Size"
									placeholder=""
									value={values.householdSize}
									onChange={(e) => setFieldValue('householdSize', e)}
									error={touched.householdSize && errors.householdSize}
								/>
							</Flex>
						</>
					);
				}}
			</Form>
		</ContentPanel>
	);
};
